import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  TextField,
} from '@mui/material';
import {
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  Flag as FlagIcon,
  Timer as TimerIcon,
  Quiz as QuizIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Send as SubmitIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuiz } from '../../contexts/QuizContext';
import { useAuth } from '../../contexts/AuthContext';
import { CONFIG } from '../../config/constants';
import ContentRenderer from '../common/ContentRenderer';

interface QuizTakerProps {
  quizId?: number;
}

interface UserAnswer {
  question_id: number;
  selected_answer_id: string;
}

const QuizTaker: React.FC<QuizTakerProps> = ({ quizId: propQuizId }) => {
  const navigate = useNavigate();
  const { id: paramQuizId } = useParams<{ id: string }>();
  const quizId = propQuizId || (paramQuizId ? parseInt(paramQuizId) : null);
  const { user, isLoading: authLoading } = useAuth();

  const {
    takingQuiz,
    currentAttempt,
    takingLoading,
    takingError,
    startTakingQuiz,
    submitQuizAnswers,
    setTakingError,
    clearTakingState,
  } = useQuiz();

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [questionId: number]: UserAnswer }>({});
  const [flaggedQuestions, setFlaggedQuestions] = useState<Set<number>>(new Set());
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showTimeWarning, setShowTimeWarning] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);
  const [showGuestDialog, setShowGuestDialog] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [loginRequired, setLoginRequired] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const handleAutoSubmit = useCallback(async () => {
    if (!currentAttempt || !quizId) return;

    const validAnswers = Object.values(answers).filter(answer =>
      answer && answer.selected_answer_id
    );

    // Cho phép auto submit ngay cả khi chưa có câu trả lời (hết giờ)
    await submitQuizAnswers(quizId, {
      attempt_id: currentAttempt.id,
      answers: validAnswers,
    });

    navigate(`/quiz/${quizId}/results/${currentAttempt.id}`);
  }, [currentAttempt, answers, submitQuizAnswers, navigate, quizId]);

  // Reset state when component mounts (fresh page load or navigation)
  useEffect(() => {
    console.log('QuizTaker mounted - resetting state for fresh start');
    // Reset all state on mount to ensure clean slate
    clearTakingState();
    setCurrentQuestionIndex(0);
    setAnswers({});
    setFlaggedQuestions(new Set());
    setTimeRemaining(null);
    setShowSubmitDialog(false);
    setShowTimeWarning(false);
    setQuizStarted(false);
    setShowGuestDialog(false);
    setGuestName('');
    setGuestEmail('');
    setLoginRequired(false);
    setInitialized(false);
  }, []); // Only run once on mount

  // Cleanup effect - reset local state when component unmounts or quizId changes
  useEffect(() => {
    return () => {
      console.log('QuizTaker cleanup - resetting local and context state');
      // Clear local state
      setCurrentQuestionIndex(0);
      setAnswers({});
      setFlaggedQuestions(new Set());
      setTimeRemaining(null);
      setShowSubmitDialog(false);
      setShowTimeWarning(false);
      setQuizStarted(false);
      setShowGuestDialog(false);
      setGuestName('');
      setGuestEmail('');
      setLoginRequired(false);
      setInitialized(false);

      // Clear context state to ensure fresh start next time
      clearTakingState();
    };
  }, [quizId, clearTakingState]);

  // Initialize quiz on mount
  useEffect(() => {
    console.log('Initialize quiz effect:', {
      quizId,
      takingQuiz: !!takingQuiz,
      currentAttempt: !!currentAttempt,
      quizStarted,
      initialized,
      authLoading,
      user: !!user,
      takingLoading
    });

    // Start quiz if:
    // 1. We have a quizId
    // 2. Auth loading is complete
    // 3. Not currently loading
    // 4. Haven't initialized yet OR (no takingQuiz and no currentAttempt - fresh start needed)
    if (quizId && !authLoading && !takingLoading &&
      (!initialized || (!takingQuiz && !currentAttempt)) &&
      !quizStarted) {
      console.log('Starting quiz initialization...');
      setInitialized(true);
      handleStartQuiz();
    }
  }, [quizId, takingQuiz, currentAttempt, user, authLoading, initialized, takingLoading, quizStarted]);

  // Note: currentAttempt will be set by the context when startTakingQuiz is called
  // We don't need to manually set it here as the backend will handle the attempt creation

  // Initialize answers when takingQuiz is loaded
  useEffect(() => {
    console.log('TakingQuiz state changed:', {
      takingQuiz: !!takingQuiz,
      hasQuestions: takingQuiz?.questions?.length || 0,
      answersLength: Object.keys(answers).length,
      quizStarted
    });

    if (takingQuiz && takingQuiz.questions && Object.keys(answers).length === 0 && quizStarted) {
      console.log('Quiz loaded, initializing answers:', {
        id: takingQuiz.id,
        title: takingQuiz.title,
        time_limit: takingQuiz.time_limit,
        total_questions: takingQuiz.questions.length
      });

      const initialAnswers: { [questionId: number]: UserAnswer } = {};
      takingQuiz.questions.forEach(q => {
        initialAnswers[q.id] = { question_id: q.id, selected_answer_id: '' }; // Empty string for no answer
      });
      setAnswers(initialAnswers);
    }
  }, [takingQuiz, answers, quizStarted]);

  // Timer effect
  useEffect(() => {
    if (!takingQuiz || !currentAttempt || timeRemaining === null) return;

    // Thêm protection: không auto-submit nếu quiz vừa mới bắt đầu (trong vòng 5 giây)
    const now = Date.now();
    const startTime = new Date(currentAttempt.started_at).getTime();
    const elapsedMs = now - startTime;

    if (timeRemaining <= 0) {
      // Chỉ auto-submit nếu đã làm bài ít nhất 5 giây
      if (elapsedMs > 5000) {
        console.log('Auto-submitting quiz due to time limit');
        handleAutoSubmit();
      } else {
        console.log('Quiz just started, not auto-submitting yet. Elapsed:', elapsedMs, 'ms');
        // Reset timer với thời gian còn lại thực tế
        if (takingQuiz.time_limit) {
          const correctRemaining = Math.max(1, takingQuiz.time_limit * 60 - Math.floor(elapsedMs / 1000));
          setTimeRemaining(correctRemaining);
        }
      }
      return;
    }

    // Show warning when 5 minutes remaining
    if (timeRemaining === 300 && !showTimeWarning) {
      setShowTimeWarning(true);
    }

    const timer = setInterval(() => {
      setTimeRemaining(prev => prev !== null ? prev - 1 : null);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, takingQuiz, currentAttempt, showTimeWarning, handleAutoSubmit]);

  // Initialize timer when quiz starts
  useEffect(() => {
    if (takingQuiz && takingQuiz.time_limit && currentAttempt) {
      const startTime = new Date(currentAttempt.started_at).getTime();
      const currentTime = Date.now();
      const elapsedSeconds = Math.floor((currentTime - startTime) / 1000);
      const totalSeconds = takingQuiz.time_limit * 60;

      // Thêm protection: nếu thời gian elapsed âm hoặc quá lớn, reset về full time
      let remaining;
      if (elapsedSeconds < 0 || elapsedSeconds > totalSeconds) {
        console.warn('Invalid elapsed time detected, resetting to full duration');
        remaining = totalSeconds;
      } else {
        remaining = Math.max(0, totalSeconds - elapsedSeconds);
      }

      console.log('Timer Debug:', {
        startTime: new Date(currentAttempt.started_at),
        currentTime: new Date(currentTime),
        elapsedSeconds,
        totalSeconds,
        remaining,
        timeLimit: takingQuiz.time_limit
      });

      setTimeRemaining(remaining);
    } else if (takingQuiz && !takingQuiz.time_limit) {
      // Quiz has no time limit
      setTimeRemaining(null);
      console.log('Quiz has no time limit');
    }
  }, [takingQuiz, currentAttempt]);

  const handleStartQuiz = async () => {
    console.log('handleStartQuiz called:', { quizId, user: !!user });

    if (!quizId) return;

    // Clear any existing quiz state to ensure fresh start
    clearTakingState();

    const token = localStorage.getItem('auth_token');
    console.log('Auth check:', { hasUser: !!user, hasToken: !!token });

    // If user is not logged in or no valid token
    if (!user || !token) {
      console.log('User not logged in, checking quiz requirements...');
      // Check if quiz requires login by trying to get quiz info first
      try {
        const response = await fetch(`${CONFIG.API.API_URL}/quiz/${quizId}`);
        const data = await response.json();

        console.log('Quiz requirements response:', { status: response.status, data });

        if (response.status === 401 && data.message === 'Login required to take this quiz') {
          // Quiz requires login - show error message
          console.log('Quiz requires login');
          setLoginRequired(true);
          return;
        } else if (response.ok) {
          // Quiz doesn't require login - show guest dialog
          console.log('Quiz allows guest access');
          setShowGuestDialog(true);
          return;
        } else {
          console.error('Unexpected response:', response.status, data);
          setShowGuestDialog(true);
          return;
        }
      } catch (error) {
        console.error('Error checking quiz requirements:', error);
        setShowGuestDialog(true);
        return;
      }
    }

    console.log('Starting quiz for authenticated user...');
    setQuizStarted(true);
    const result = await startTakingQuiz(quizId);
    console.log('startTakingQuiz result:', result);
  };

  const handleGuestSubmit = async () => {
    if (!guestName.trim()) {
      return; // Name is required
    }

    setShowGuestDialog(false);
    setQuizStarted(true);

    await startTakingQuiz(quizId!, {
      name: guestName.trim(),
      email: guestEmail.trim() || undefined
    });
  };

  const handleAnswerChange = (questionId: number, answerId: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        question_id: questionId,
        selected_answer_id: answerId
      }
    }));
  };

  const handleQuestionNavigation = (direction: 'next' | 'prev' | number) => {
    if (!takingQuiz) return;

    let newIndex = currentQuestionIndex;

    if (direction === 'next') {
      newIndex = Math.min(currentQuestionIndex + 1, takingQuiz.questions.length - 1);
    } else if (direction === 'prev') {
      newIndex = Math.max(currentQuestionIndex - 1, 0);
    } else {
      newIndex = direction;
    }

    setCurrentQuestionIndex(newIndex);
  };

  const handleFlagQuestion = (questionIndex: number) => {
    const newFlagged = new Set(flaggedQuestions);
    if (newFlagged.has(questionIndex)) {
      newFlagged.delete(questionIndex);
    } else {
      newFlagged.add(questionIndex);
    }
    setFlaggedQuestions(newFlagged);
  };

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmitQuiz = async () => {
    if (!currentAttempt || !quizId) return;

    const validAnswers = Object.values(answers).filter(answer =>
      answer && answer.selected_answer_id
    );

    const result = await submitQuizAnswers(quizId, {
      attempt_id: currentAttempt.id,
      answers: validAnswers,
    });

    if (result) {
      setShowSubmitDialog(false);
      navigate(`/quiz/${quizId}/results/${currentAttempt.id}`);
    }
  };

  // Find the answer for a question (by questionId)
  const getAnswerForQuestion = (questionId: number) => {
    return answers[questionId];
  };

  // Get the selected answer index for a question (0-based for UI)
  const getSelectedAnswerIndex = (questionId: number): number => {
    const answer = answers[questionId];
    if (!answer || !answer.selected_answer_id) return -1;

    // Extract index from "option_X" format
    const match = answer.selected_answer_id.match(/^option_(\d+)$/);
    return match ? parseInt(match[1]) - 1 : -1; // Convert to 0-based index
  };

  // Get the selected answer ID for a question (e.g., "option_1")
  const getSelectedAnswerId = (questionId: number): string => {
    const answer = answers[questionId];
    return answer && answer.selected_answer_id ? answer.selected_answer_id : '';
  };

  // Get the selected answer index for a question by index (for backward compatibility)
  const getSelectedAnswerIndexByIndex = (questionIndex: number): number => {
    if (!takingQuiz || !takingQuiz.questions[questionIndex]) return -1;
    const questionId = takingQuiz.questions[questionIndex].id;
    return getSelectedAnswerIndex(questionId);
  };

  const getAnsweredCount = (): number => {
    if (!answers || typeof answers !== 'object') return 0;
    return Object.values(answers).filter(answer =>
      answer && answer.selected_answer_id
    ).length;
  };

  const getTotalQuestions = (): number => {
    return takingQuiz?.questions?.length || 0;
  };

  const getProgressPercentage = (): number => {
    const total = getTotalQuestions();
    return total > 0 ? (getAnsweredCount() / total) * 100 : 0;
  };

  // Loading state (auth or quiz)
  if (takingLoading || authLoading) {
    const loadingMessage = authLoading
      ? 'Đang kiểm tra đăng nhập...'
      : takingLoading && !takingQuiz
        ? 'Đang tải thông tin bài tập...'
        : takingLoading && !currentAttempt
          ? 'Đang khởi tạo phiên làm bài...'
          : 'Đang tải bài tập...';

    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Box textAlign="center">
            <QuizIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {loadingMessage}
            </Typography>
            {takingLoading && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Quiz ID: {quizId}
              </Typography>
            )}
          </Box>
        </Box>
      </Container>
    );
  }

  // Login required state
  if (loginRequired) {
    return (
      <Container maxWidth="md">
        <Box py={4}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Bài tập này yêu cầu đăng nhập để có thể làm bài. Vui lòng đăng nhập để tiếp tục.
          </Alert>
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
            >
              Đăng nhập
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/quizzes')}
            >
              Quay lại danh sách bài tập
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  // Error state
  if (takingError) {
    return (
      <Container maxWidth="md">
        <Box py={4}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {takingError}
          </Alert>
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              onClick={() => {
                console.log('Retrying quiz initialization...');
                // Reset all states for fresh retry
                clearTakingState();
                setInitialized(false);
                setQuizStarted(false);
                setTakingError(null);
                setCurrentQuestionIndex(0);
                setAnswers({});
                setFlaggedQuestions(new Set());
                setTimeRemaining(null);
              }}
            >
              Thử lại
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/quizzes')}
            >
              Quay lại danh sách bài tập
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  // Show guest dialog if needed
  if (showGuestDialog) {
    return (
      <Container maxWidth="md">
        <Box py={4}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Bài tập này không yêu cầu đăng nhập. Vui lòng nhập thông tin để bắt đầu làm bài.
          </Alert>

          {/* Guest Information Dialog */}
          <Dialog
            open={showGuestDialog}
            onClose={() => setShowGuestDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Thông tin người làm bài</DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Bài tập này không yêu cầu đăng nhập. Vui lòng nhập thông tin của bạn để bắt đầu làm bài:
                </Typography>

                <TextField
                  label="Họ và tên *"
                  fullWidth
                  margin="normal"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  error={!guestName.trim() && guestName !== ''}
                  helperText={!guestName.trim() && guestName !== '' ? 'Họ và tên là bắt buộc' : ''}
                  autoFocus
                />

                <TextField
                  label="Email (tùy chọn)"
                  type="email"
                  fullWidth
                  margin="normal"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  helperText="Email để nhận thông báo kết quả (không bắt buộc)"
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowGuestDialog(false)}>
                Hủy
              </Button>
              <Button
                variant="contained"
                onClick={handleGuestSubmit}
                disabled={!guestName.trim() || takingLoading}
              >
                {takingLoading ? 'Đang bắt đầu...' : 'Bắt đầu làm bài'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
    );
  }

  // Quiz not loaded (only show this if we've tried to initialize but failed)
  if (initialized && quizStarted && (!takingQuiz || !currentAttempt)) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Box textAlign="center">
            <QuizIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Không thể tải bài tập
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Quiz ID: {quizId}
            </Typography>
            <Box display="flex" gap={2} justifyContent="center">
              <Button
                variant="contained"
                onClick={() => {
                  console.log('Retrying quiz load...');
                  clearTakingState();
                  setInitialized(false);
                  setQuizStarted(false);
                }}
              >
                Thử lại
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/quizzes')}
              >
                Quay lại danh sách bài tập
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>
    );
  }

  // At this point, we know takingQuiz and currentAttempt are not null due to the checks above
  if (!takingQuiz || !currentAttempt) {
    return null; // This should never happen due to checks above, but satisfies TypeScript
  }

  const currentQuestion = takingQuiz.questions[currentQuestionIndex];
  const currentAnswer = getAnswerForQuestion(currentQuestion.id);

  return (
    <Container maxWidth="lg">
      <Box py={2}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" component="h1">
              {takingQuiz.title}
            </Typography>
            {timeRemaining !== null && (
              <Chip
                icon={<TimerIcon />}
                label={formatTime(timeRemaining)}
                color={timeRemaining <= 300 ? 'error' : timeRemaining <= 900 ? 'warning' : 'primary'}
                variant="outlined"
              />
            )}
          </Box>

          {takingQuiz.description && (
            <Box sx={{ mb: 2, color: 'text.secondary' }}>
              <ContentRenderer text={takingQuiz.description} display="block" />
            </Box>
          )}

          {/* Progress */}
          <Box mb={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2">
                Câu hỏi {currentQuestionIndex + 1} / {takingQuiz.questions.length}
              </Typography>
              <Typography variant="body2">
                Đã trả lời: {getAnsweredCount()} / {takingQuiz.questions.length}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={getProgressPercentage()}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        </Paper>

        <Box display="flex" gap={3}>
          {/* Question Panel */}
          <Box flex={1}>
            <Card>
              <CardContent>
                {/* Question Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6">
                    Câu {currentQuestionIndex + 1}
                  </Typography>
                  <Box>
                    <Tooltip title={flaggedQuestions.has(currentQuestionIndex) ? "Bỏ đánh dấu" : "Đánh dấu để xem lại"}>
                      <IconButton
                        onClick={() => handleFlagQuestion(currentQuestionIndex)}
                        color={flaggedQuestions.has(currentQuestionIndex) ? 'warning' : 'default'}
                      >
                        <FlagIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Question Text */}
                <Box sx={{ mb: 3, lineHeight: 1.6 }}>
                  <ContentRenderer text={currentQuestion.text} display="block" />
                </Box>

                {/* Answer Options */}
                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    value={getSelectedAnswerId(currentQuestion.id)}
                    onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                  >
                    {(Array.isArray(currentQuestion.answer_objects) ? currentQuestion.answer_objects : []).map((answerObject, index: number) => (
                      <FormControlLabel
                        key={index}
                        value={answerObject.id}
                        control={<Radio />}
                        label={
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <Typography variant="body1" component="span" sx={{ mr: 1, minWidth: '24px' }}>
                              {String.fromCharCode(65 + index)}.
                            </Typography>
                            <ContentRenderer text={answerObject.text} display="inline" />
                          </Box>
                        }
                        sx={{
                          mb: 1,
                          p: 1,
                          borderRadius: 1,
                          border: '1px solid transparent',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            borderColor: 'primary.main',
                          },
                          ...(getSelectedAnswerId(currentQuestion.id) === answerObject.id && {
                            backgroundColor: 'primary.light',
                            borderColor: 'primary.main',
                          }),
                        }}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>

                {/* Navigation */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={4}>
                  <Button
                    startIcon={<PrevIcon />}
                    onClick={() => handleQuestionNavigation('prev')}
                    disabled={currentQuestionIndex === 0}
                  >
                    Câu trước
                  </Button>

                  <Box display="flex" gap={1}>
                    {currentQuestionIndex === takingQuiz.questions.length - 1 ? (
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<SubmitIcon />}
                        onClick={() => setShowSubmitDialog(true)}
                      >
                        Nộp bài
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        endIcon={<NextIcon />}
                        onClick={() => handleQuestionNavigation('next')}
                      >
                        Câu tiếp theo
                      </Button>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Question Navigator Sidebar */}
          <Box width={280}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Điều hướng câu hỏi
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: 1,
                    mb: 3,
                  }}
                >
                  {takingQuiz.questions.map((_, index) => {
                    const isAnswered = getSelectedAnswerIndexByIndex(index) >= 0;
                    const isFlagged = flaggedQuestions.has(index);
                    const isCurrent = index === currentQuestionIndex;

                    return (
                      <Button
                        key={index}
                        variant={isCurrent ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => handleQuestionNavigation(index)}
                        sx={{
                          minWidth: 40,
                          height: 40,
                          position: 'relative',
                          backgroundColor: isCurrent
                            ? 'primary.main'
                            : isAnswered
                              ? 'success.light'
                              : 'background.paper',
                          color: isCurrent
                            ? 'primary.contrastText'
                            : isAnswered
                              ? 'success.contrastText'
                              : 'text.primary',
                          '&:hover': {
                            backgroundColor: isCurrent
                              ? 'primary.dark'
                              : isAnswered
                                ? 'success.main'
                                : 'action.hover',
                          },
                        }}
                      >
                        {index + 1}
                        {isFlagged && (
                          <FlagIcon
                            sx={{
                              position: 'absolute',
                              top: -4,
                              right: -4,
                              fontSize: 12,
                              color: 'warning.main',
                            }}
                          />
                        )}
                      </Button>
                    );
                  })}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Legend */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Chú thích:
                  </Typography>
                  <Box display="flex" alignItems="center" mb={1}>
                    <CheckIcon color="success" sx={{ mr: 1, fontSize: 16 }} />
                    <Typography variant="body2">Đã trả lời</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" mb={1}>
                    <UncheckedIcon color="disabled" sx={{ mr: 1, fontSize: 16 }} />
                    <Typography variant="body2">Chưa trả lời</Typography>
                  </Box>
                  <Box display="flex" alignItems="center">
                    <FlagIcon color="warning" sx={{ mr: 1, fontSize: 16 }} />
                    <Typography variant="body2">Đánh dấu xem lại</Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Submit Button */}
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  startIcon={<SubmitIcon />}
                  onClick={() => setShowSubmitDialog(true)}
                >
                  Nộp bài
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>

      {/* Guest Information Dialog */}
      <Dialog
        open={showGuestDialog}
        onClose={() => setShowGuestDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Thông tin người làm bài</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Bài tập này không yêu cầu đăng nhập. Vui lòng nhập thông tin của bạn để bắt đầu làm bài:
            </Typography>

            <TextField
              label="Họ và tên *"
              fullWidth
              margin="normal"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              error={!guestName.trim() && guestName !== ''}
              helperText={!guestName.trim() && guestName !== '' ? 'Họ và tên là bắt buộc' : ''}
              autoFocus
            />

            <TextField
              label="Email (tùy chọn)"
              type="email"
              fullWidth
              margin="normal"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              helperText="Email để nhận thông báo kết quả (không bắt buộc)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowGuestDialog(false)}>
            Hủy
          </Button>
          <Button
            variant="contained"
            onClick={handleGuestSubmit}
            disabled={!guestName.trim() || takingLoading}
          >
            {takingLoading ? 'Đang bắt đầu...' : 'Bắt đầu làm bài'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Time Warning Dialog */}
      <Dialog
        open={showTimeWarning}
        onClose={() => setShowTimeWarning(false)}
      >
        <DialogTitle>Cảnh báo thời gian</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Bạn chỉ còn 5 phút để hoàn thành bài tập. Hãy kiểm tra lại các câu trả lời và nộp bài sớm.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTimeWarning(false)}>
            Đã hiểu
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submit Confirmation Dialog */}
      <Dialog
        open={showSubmitDialog}
        onClose={() => setShowSubmitDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Xác nhận nộp bài</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography variant="body1" gutterBottom>
              Bạn có chắc chắn muốn nộp bài không?
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Sau khi nộp bài, bạn sẽ không thể thay đổi câu trả lời.
            </Typography>
          </Box>

          {getAnsweredCount() === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Bạn chưa trả lời câu hỏi nào. Bạn có chắc chắn muốn nộp bài trống không?
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Tình trạng:
            </Typography>
            <Typography variant="body2">
              • Đã trả lời: {getAnsweredCount()} / {takingQuiz?.questions.length || 0} câu
            </Typography>
            <Typography variant="body2">
              • Chưa trả lời: {(takingQuiz?.questions.length || 0) - getAnsweredCount()} câu
            </Typography>
            {flaggedQuestions.size > 0 && (
              <Typography variant="body2">
                • Đánh dấu xem lại: {flaggedQuestions.size} câu
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSubmitDialog(false)}>
            Hủy
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmitQuiz}
            disabled={takingLoading}
          >
            {takingLoading ? 'Đang nộp...' : 'Nộp bài'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default QuizTaker;
